import * as React from "react";
import { Fade, Slide, styled } from "@mui/material";
import { ExternalBackgroundOverlay } from "../../uiControllers/extrenalBackgroundOverlay";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { ReactComponent as KahunIcon } from "../../../assets/kahunIconLight.svg";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { GenericButton } from "../../uiControllers/genericButton";
import { useTranslate } from "../../../hooks/useTranslate";
import { DemoPanelTooltip } from "./demoPanelTooltip";
import { useDemoReasoningController } from "./useDemoReasoningController";
import { DemoZoomContentSwitch } from "./demoZoomContentSwitch";

//region [[ Styles ]]
const DarkBackground = styled(ExternalBackgroundOverlay)({
  margin: 0,
});

const POPUP_PADDING_LR = 30;

const MobileSuggestionZoomView = styled("div")({
  background: KahunColorMap.White,
  borderTopRightRadius: "14px",
  borderTopLeftRadius: "14px",
  padding: `${POPUP_PADDING_LR}px 15px`,
  paddingBottom: 0,
  maxHeight: "95vh",
  // Give absolute children a place to relate to
  position: "relative",
});

const GotItContainer = styled("div")({
  width: `calc(100% + ${POPUP_PADDING_LR}px)`,
  background: PatientColorMap.Primary,
  margin: `0 -${POPUP_PADDING_LR / 2}px`,
  marginTop: "5px",
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  justifyContent: "center",
});

const GotItButton = styled(GenericButton)({
  "&.generic-button": {
    width: "25%",
    backgroundColor: PatientColorMap.Unselected,
    color: PatientColorMap.Default,
    margin: "10px",
  },
});

const CloseButton = styled(CloseIconButton)({
  "&.close-button.demo-close-icon": {
    right: "15px",
    left: "auto",
  },
});

const GoBackButtonWrapper = styled(IconButton)({
  "&.back": {
    position: "absolute",
    left: "15px",
    "&:hover, &:focus, &": {
      padding: "12px",
      background: "transparent",
    },
  },
});

const TopArea = styled("div")({
  display: "flex",
  flexFlow: "column",
  padding: "0 15%",
  paddingBottom: "10px",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MobileSuggestionZoomProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MobileSuggestionZoom = ({
  ...props
}: MobileSuggestionZoomProps) => {
  const { translate } = useTranslate();

  const {
    open,
    onCloseClick,
    currentView,
    loadingMessage,
    onDiseaseInfoClick,
    onReferencesClick,
    currentSelectedDisease,
    onGoBack,
    onShowGraph,
    allowBack,
  } = useDemoReasoningController();

  return (
    <Fade in={open} timeout={50} mountOnEnter unmountOnExit>
      <DarkBackground>
        <Slide
          direction="up"
          in={open}
          timeout={{ appear: 200, enter: 700 }}
          mountOnEnter
          unmountOnExit
        >
          <MobileSuggestionZoomView>
            <CloseButton className={"demo-close-icon"} onClick={onCloseClick} />
            <TopArea>
              <Fade in={allowBack} mountOnEnter unmountOnExit>
                <GoBackButtonWrapper className={"back"} onClick={onGoBack}>
                  <ArrowBackIosIcon />
                </GoBackButtonWrapper>
              </Fade>
              <KahunIcon />
              <DemoPanelTooltip />
            </TopArea>
            <DemoZoomContentSwitch
              currentView={currentView}
              loadingMessage={loadingMessage}
              onDiseaseInfoClick={onDiseaseInfoClick}
              onReferencesClick={onReferencesClick}
              currentSelectedDisease={currentSelectedDisease}
              onShowGraph={onShowGraph}
            />
            <GotItContainer>
              <GotItButton label={translate("GOT_IT")} onClick={onCloseClick} />
            </GotItContainer>
          </MobileSuggestionZoomView>
        </Slide>
      </DarkBackground>
    </Fade>
  );
};
